import { DOCUMENT_VOE_LINK_FIELDS } from './documents.graphql'
import {
  AI_EXTRACTED_ROW_FIELDS_FRAGMENT,
  AI_PREDICTION_FIELDS_FRAGMENT,
} from './fragments'

export const GET_STEPS = gql`
  query Steps(
    $applicantId: String!
    $pageInfo: PageInfoInput
    $filters: StepFiltersInput
  ) {
    steps(applicantId: $applicantId, pageInfo: $pageInfo, filters: $filters) {
      totalCount
      items {
        stepId
        createdAt
        updatedAt
        status
        type
        customStepId
        customStep {
          label
        }
        documents {
          documentId
          createdAt
          updatedAt
          deletedAt
          status
          userStatus
          uploadStatus
          category
          fileType
          expirationDate
          fileName
          path
          uploadUrl
          uploadedBy {
            employeeId
            firstName
            lastName
          }
          expirationTrackers {
            expirationTrackerId
            status
          }
        }
      }
    }
  }
`

// Sidebar checklist data, split out of GET_APPLICANT_BY_ID so the heavy
// steps → documents → documentIntelligence subtree doesn't block the
// applicant header (name, stage, contact info) from rendering.
export const GET_SIDEBAR_CHECKLIST_STEPS = gql`
  ${AI_PREDICTION_FIELDS_FRAGMENT}
  ${AI_EXTRACTED_ROW_FIELDS_FRAGMENT}
  query SidebarChecklistSteps($applicantId: String!) {
    steps(applicantId: $applicantId) {
      totalCount
      items {
        stepId
        createdAt
        updatedAt
        status
        type
        customStepId
        customStep {
          label
        }
        documents {
          documentId
          status
          userStatus
          category
          expirationDate
          fileName
          documentIntelligence {
            status
            predictions {
              ...AiPredictionFields
            }
            findings {
              findingGroupId
              findingType
              fields {
                field
                value
              }
            }
            aiExtractedRows {
              ...AiExtractedRowFields
            }
          }
        }
      }
    }
  }
`

// Lightweight variant of GET_STEPS_GROUP_BY_TYPE for populating checklist-item
// pickers (edit document modal, document preview). Deliberately selects no
// documents or AI fields — it only needs enough to render a label per step.
export const GET_STEP_OPTIONS = gql`
  query StepOptions(
    $applicantId: String!
    $stepTypes: [StepType!]
    $pageInfo: PageInfoInput
  ) {
    stepsGroupByType(
      applicantId: $applicantId
      stepTypes: $stepTypes
      pageInfo: $pageInfo
    ) {
      items {
        stepId
        type
        customStepId
        customStep {
          label
        }
      }
    }
  }
`

export const UPDATE_STEP = gql`
  mutation UpdateStep($id: String!, $input: UpdateStepInput!) {
    updateStep(id: $id, input: $input) {
      stepId
      createdAt
      updatedAt
      status
      type
      documents {
        documentId
        createdAt
        updatedAt
        deletedAt
        status
        userStatus
        uploadStatus
        category
        fileType
        expirationDate
        fileName
        path
        uploadUrl
      }
    }
  }
`

export const GET_STEPS_GROUP_BY_TYPE = gql`
  ${DOCUMENT_VOE_LINK_FIELDS}
  ${AI_PREDICTION_FIELDS_FRAGMENT}
  ${AI_EXTRACTED_ROW_FIELDS_FRAGMENT}
  query StepsGroupByType(
    $applicantId: String!
    $stepTypes: [StepType!]
    $pageInfo: PageInfoInput
  ) {
    stepsGroupByType(
      applicantId: $applicantId
      stepTypes: $stepTypes
      pageInfo: $pageInfo
    ) {
      totalCount
      items {
        stepId
        createdAt
        updatedAt
        status
        type
        customStepId
        customStep {
          label
        }
        documents {
          documentId
          createdAt
          updatedAt
          deletedAt
          status
          userStatus
          uploadStatus
          category
          fileType
          expirationDate
          fileName
          path
          uploadUrl
          isRenewed
          uploadedBy {
            employeeId
            firstName
            lastName
          }
          voe {
            ...DocumentVoELinkFields
          }
          expirationTrackers {
            expirationTrackerId
            status
          }
          documentIntelligence {
            status
            predictions {
              ...AiPredictionFields
            }
            findings {
              findingGroupId
              findingType
              fields {
                field
                value
              }
            }
            aiExtractedRows {
              ...AiExtractedRowFields
            }
          }
        }
      }
    }
  }
`

export const GET_STEPS_METRICS = gql`
  query StepsMetrics($filters: StepFiltersInput) {
    stepMetrics(filters: $filters) {
      totalCount
      items {
        name
        customStepId
        customStepLabel
        value
      }
    }
  }
`

export const APPLICANT_STEP_SYNC_PREVIEW = gql`
  query ApplicantStepSyncPreview($applicantId: String!) {
    applicantStepSyncPreview(applicantId: $applicantId) {
      toAdd {
        stepType
        customStepId
        label
      }
      toRemove {
        stepType
        customStepId
        label
      }
    }
  }
`

export const SYNC_APPLICANT_STEPS_TO_TEMPLATE = gql`
  mutation SyncApplicantStepsToTemplate($applicantId: String!) {
    syncApplicantStepsToTemplate(applicantId: $applicantId) {
      toAdd {
        stepType
        customStepId
        label
      }
      toRemove {
        stepType
        customStepId
        label
      }
    }
  }
`
